/**
 * Componente: Formulario Paso 3 — Acceso y Seguridad (#EE5847 Coral)
 * Estilo Fiel y Consistente con Paso 1 y Paso 2 de AviFácil
 */

'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useForm, FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ROUTES } from '@/config/routes';
import { paso3CredencialesSchema, Paso3CredencialesSchemaType } from '../schemas/onboarding.schema';
import { Paso3CredencialesFormData } from '../types/onboarding.types';
import { ValidationState } from '../store/onboarding.store';
import { PasswordEntropyMeter } from './PasswordEntropyMeter';
import { TerminosCondicionesModal } from './TerminosCondicionesModal';
import { PorQuePedimosDatosModal } from './PorQuePedimosDatosModal';
import styles from './Step3CredencialesForm.module.css';

interface Step3CredencialesFormProps {
  initialData: Paso3CredencialesFormData;
  paso2Email?: string;
  emailValidation: ValidationState;
  isSubmitting: boolean;
  onSubmit: (data: Paso3CredencialesFormData) => Promise<boolean>;
  onValidarEmail: (email: string) => Promise<void>;
  onBack: () => void;
}

export function Step3CredencialesForm({
  initialData,
  paso2Email,
  emailValidation,
  isSubmitting,
  onSubmit,
  onValidarEmail,
  onBack,
}: Step3CredencialesFormProps) {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [isTerminosModalOpen, setIsTerminosModalOpen] = useState<boolean>(false);
  const [showInfoModal, setShowInfoModal] = useState<boolean>(false);

  const emailEfectivo = (initialData.email || paso2Email || '').trim();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    trigger,
    formState: { errors },
  } = useForm<Paso3CredencialesSchemaType>({
    resolver: zodResolver(paso3CredencialesSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      email: emailEfectivo,
      telefono_movil: initialData.telefono_movil || '',
      password: initialData.password || '',
      confirm_password: initialData.confirm_password || '',
      acepta_terminos_y_condiciones: initialData.acepta_terminos_y_condiciones ?? false,
    },
  });

  const emailValue = watch('email');
  const passwordValue = watch('password') || '';
  const confirmPasswordValue = watch('confirm_password') || '';

  // Mantener sincronizado el email efectivo si initialData o paso2Email cambian
  useEffect(() => {
    if (emailEfectivo) {
      setValue('email', emailEfectivo, { shouldValidate: true });
      clearErrors('email');
      onValidarEmail(emailEfectivo);
    }
  }, [emailEfectivo, setValue, clearErrors, onValidarEmail]);

  // Revalidar confirmación de contraseña en tiempo real cuando se corrige cualquier campo
  useEffect(() => {
    if (confirmPasswordValue) {
      trigger('confirm_password');
    }
  }, [passwordValue, confirmPasswordValue, trigger]);

  // Centinela anti-colisión en debounce (400ms)
  useEffect(() => {
    if (!emailValue || emailValue.length < 5 || !emailValue.includes('@')) return;
    const timer = setTimeout(() => {
      onValidarEmail(emailValue);
    }, 400);

    return () => clearTimeout(timer);
  }, [emailValue, onValidarEmail]);

  const onFormSubmit = async (data: Paso3CredencialesSchemaType) => {
    const dataToSend = {
      ...data,
      email: data.email || emailEfectivo,
    };
    await onSubmit(dataToSend as Paso3CredencialesFormData);
  };

  const onFormInvalid = (formErrors: FieldErrors<Paso3CredencialesSchemaType>) => {
    const errorKeys = Object.keys(formErrors) as (keyof Paso3CredencialesSchemaType)[];
    if (errorKeys.length > 0) {
      const firstKey = errorKeys[0];
      const firstMsg =
        formErrors[firstKey]?.message || 'Por favor completa todos los campos requeridos.';
      toast.error(firstMsg);
    }
  };

  return (
    <div className={styles.formWrapper}>
      {/* ─── Encabezado Principal ─── */}
      <div className="text-center mb-2 sm:mb-3">
        <h1
          className="text-[28px] sm:text-[34px] lg:text-[38px] leading-none font-bold text-gray-900 tracking-normal"
          style={{ fontFamily: "'Caveat', cursive", fontWeight: 700, lineHeight: 1.05 }}
        >
          Acceso y <span className={styles.highlightMarker}>Seguridad</span>
        </h1>
        <p className="text-xs sm:text-sm text-gray-500 mt-1 max-w-sm mx-auto">
          Crea tu contraseña para ingresar a tu panel de control y proteger tu granja.
        </p>
      </div>

      {/* ─── Stepper Paso 3 Debajo del Título ─── */}
      <nav className={styles.stepperNav} aria-label="Progreso de registro">
        <div className={styles.stepperContainer}>
          {/* Paso 1: Completado (Check Coral) */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <span className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#EE5847] text-white flex items-center justify-center text-[10px] sm:text-xs font-bold shadow-xs">
              <span className="material-symbols-outlined text-[13px] sm:text-[14px]">check</span>
            </span>
            <span className="text-xs sm:text-sm font-semibold text-[#EE5847] tracking-tight">
              Granja
            </span>
          </div>

          <div className="h-0.5 w-6 sm:w-10 bg-[#EE5847] rounded-full" />

          {/* Paso 2: Completado (Check Coral) */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <span className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#EE5847] text-white flex items-center justify-center text-[10px] sm:text-xs font-bold shadow-xs">
              <span className="material-symbols-outlined text-[13px] sm:text-[14px]">check</span>
            </span>
            <span className="text-xs sm:text-sm font-semibold text-[#EE5847] tracking-tight">
              Titular
            </span>
          </div>

          <div className="h-0.5 w-6 sm:w-10 bg-[#EE5847] rounded-full" />

          {/* Paso 3: Activo (Coral #EE5847) */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <span className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#EE5847] text-white flex items-center justify-center text-[10px] sm:text-xs font-bold shadow-xs">
              3
            </span>
            <span className="text-xs sm:text-sm font-bold text-gray-900 tracking-tight">
              Acceso
            </span>
          </div>
        </div>
      </nav>

      {/* ─── Formulario de Credenciales ─── */}
      <form
        onSubmit={handleSubmit(onFormSubmit, onFormInvalid)}
        className={styles.formContainer}
      >
        <input type="hidden" {...register('email')} />

        {/* Identidad de la Cuenta (Email configurado en Paso 2) */}
        {emailEfectivo && (
          <div className="bg-slate-50 border border-slate-200/80 rounded-xl px-3 py-2 flex items-center justify-between text-xs">
            <div className="flex items-center gap-2 min-w-0">
              <span className="material-symbols-outlined text-[17px] text-[#EE5847] shrink-0">
                mail
              </span>
              <span className="text-slate-600 truncate">
                Cuenta:{' '}
                <strong className="text-slate-800 font-bold">{emailEfectivo}</strong>
              </span>
            </div>
            <button
              type="button"
              onClick={onBack}
              className="text-[11px] text-[#EE5847] hover:underline font-bold shrink-0 ml-2"
              title="Volver a Paso 2 para cambiar el correo"
            >
              Cambiar
            </button>
          </div>
        )}

        {errors.email && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-2.5 text-xs text-red-600 flex items-center gap-2">
            <span className="material-symbols-outlined text-[16px] shrink-0">error</span>
            <span>{errors.email.message}</span>
          </div>
        )}
        {/* Campo 1: Contraseña de Acceso */}
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="password"
            className="text-xs font-semibold text-slate-700 tracking-wide flex items-center gap-1.5"
          >
            <span className="material-symbols-outlined text-[18px]" style={{ color: '#EE5847' }}>
              lock
            </span>
            Crea tu contraseña de acceso
          </label>
          <div className={styles.passwordWrapper}>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Mínimo 8 caracteres (letras y números)"
              className={`${styles.inputField} pr-10 ${errors.password ? styles.inputError : ''}`}
              {...register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className={styles.eyeToggleBtn}
              aria-label={showPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
            >
              <span className="material-symbols-outlined text-[20px]">
                {showPassword ? 'visibility_off' : 'visibility'}
              </span>
            </button>
          </div>
          {errors.password && (
            <span className="text-[11px] text-red-500 font-semibold px-1">
              {errors.password.message}
            </span>
          )}

          {/* Medidor de robustez de contraseña */}
          <PasswordEntropyMeter password={passwordValue} />
        </div>

        {/* Campo 3: Confirmar Contraseña */}
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="confirm-password"
            className="text-xs font-semibold text-slate-700 tracking-wide flex items-center justify-between"
          >
            <span className="flex items-center gap-1.5">
              <span className="material-symbols-outlined text-[18px]" style={{ color: '#EE5847' }}>
                lock_reset
              </span>
              Confirma tu contraseña
            </span>
            {confirmPasswordValue && passwordValue === confirmPasswordValue && (
              <span className="text-[11px] text-emerald-600 flex items-center gap-0.5 font-bold">
                <span className="material-symbols-outlined text-[14px]">check_circle</span>
                Coinciden
              </span>
            )}
          </label>
          <div className={styles.passwordWrapper}>
            <input
              id="confirm-password"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Repite tu contraseña exactamente"
              className={`${styles.inputField} pr-10 ${
                errors.confirm_password ? styles.inputError : ''
              }`}
              {...register('confirm_password')}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              className={styles.eyeToggleBtn}
              aria-label={showConfirmPassword ? 'Ocultar confirmación' : 'Ver confirmación'}
            >
              <span className="material-symbols-outlined text-[20px]">
                {showConfirmPassword ? 'visibility_off' : 'visibility'}
              </span>
            </button>
          </div>
          {errors.confirm_password && (
            <span className="text-[11px] text-red-500 font-semibold px-1">
              {errors.confirm_password.message}
            </span>
          )}
        </div>

        {/* Campo 4: Checkbox de Aceptación Legal */}
        <div className="pt-1 flex flex-col gap-1">
          <label className={styles.checkboxContainer}>
            <input
              type="checkbox"
              className={styles.checkboxInput}
              {...register('acepta_terminos_y_condiciones')}
            />
            <span className="text-[11.5px] text-slate-600 leading-snug">
              He leído y acepto los{' '}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setIsTerminosModalOpen(true);
                }}
                className="font-bold text-[#EE5847] hover:underline"
              >
                Términos y Condiciones
              </button>{' '}
              y la{' '}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setIsTerminosModalOpen(true);
                }}
                className="font-bold text-[#EE5847] hover:underline"
              >
                Política de Privacidad
              </button>{' '}
              de AviFácil.
            </span>
          </label>
          {errors.acepta_terminos_y_condiciones && (
            <span className="text-[11px] text-red-500 font-semibold px-1">
              {errors.acepta_terminos_y_condiciones.message}
            </span>
          )}
        </div>

        {/* Alerta de Correo Duplicado */}
        {emailValidation.suggestLogin && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <span className="material-symbols-outlined text-[18px] text-[#ffb800] shrink-0">
                warning
              </span>
              <span className="truncate">Este correo ya está registrado en AviFácil.</span>
            </div>
            <Link href="/login" className="font-bold text-[#c25ee8] hover:underline ml-2 shrink-0">
              Iniciar Sesión
            </Link>
          </div>
        )}

        {/* Fila 5: Botones de Acción (Volver & Finalizar) */}
        <div className="pt-2 flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            disabled={isSubmitting}
            className={styles.volverBtn}
          >
            <span className="material-symbols-outlined text-[18px] text-slate-500">
              arrow_back
            </span>
            <span>Volver</span>
          </button>

          <button
            type="submit"
            disabled={isSubmitting}
            className={styles.finalizarBtn}
          >
            {isSubmitting ? (
              <span className="inline-flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Aprovisionando Granja...</span>
              </span>
            ) : (
              <>
                <span>Crear Granja y Comenzar</span>
                <span className="material-symbols-outlined text-[18px]">
                  rocket_launch
                </span>
              </>
            )}
          </button>
        </div>

        {/* Fila 6: Enlace Informativo "¿Por qué me piden estos datos?" */}
        <div className="text-center pt-1">
          <button
            type="button"
            onClick={() => setShowInfoModal(true)}
            className="text-[11px] text-gray-500 hover:text-[#EE5847] inline-flex items-center gap-1 transition cursor-pointer select-none"
          >
            <span className="material-symbols-outlined text-[13px] text-[#EE5847]">
              help
            </span>
            <span>¿Por qué me piden estos datos?</span>
          </button>
        </div>

        {/* Fila 7: ¿Ya tienes cuenta? Inicia sesión */}
        <div className="mt-1 text-center">
          <p className="text-xs text-gray-600">
            ¿Ya tienes cuenta?{' '}
            <Link
              href="/login"
              className="text-[#EE5847] font-bold hover:underline ml-0.5"
            >
              Inicia sesión
            </Link>
          </p>
        </div>

        {/* Derechos de Autor (Visible en móvil y desktop) */}
        <div className="text-center pt-2.5 flex flex-col items-center gap-1 select-none">
          <p className="text-[10px] text-gray-400 font-normal tracking-wide">
            © 2026 AviFácil. Todos los derechos reservados.
          </p>
        </div>
      </form>

      {/* ─── Modal Informativo de Datos de Acceso ─── */}
      <PorQuePedimosDatosModal
        isOpen={showInfoModal}
        paso={3}
        onClose={() => setShowInfoModal(false)}
      />

      {/* ─── Modal de Términos y Condiciones / Política de Privacidad ─── */}
      <TerminosCondicionesModal
        isOpen={isTerminosModalOpen}
        onClose={() => setIsTerminosModalOpen(false)}
        onAccept={() => {
          setValue('acepta_terminos_y_condiciones', true, { shouldValidate: true });
          setIsTerminosModalOpen(false);
        }}
      />
    </div>
  );
}
